
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import GalleryGrid, { GalleryHero } from './components/GalleryGrid';
import { useSEO, generateBreadcrumbSchema } from '../../utils/seo';

export default function GalleryPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useSEO({
    title: 'Roseville Photo Gallery | Community & Scenic Photos | Roseville.org',
    description: 'Explore stunning photos of Roseville, California — parks, events, neighborhoods, dining, and community life. Browse our community photo gallery and share your own Roseville moments.',
    keywords: 'Roseville photos, Roseville California gallery, Roseville community pictures, Roseville parks photos, Roseville events photos',
    canonical: '/gallery',
    ogImage: 'https://public.readdy.ai/ai/img_res/0d54312bdc74a7013ce301c4562abe3f.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ImageGallery',
          name: 'Roseville Community Photo Gallery',
          description: 'Community and scenic photos of Roseville, California including parks, events, neighborhoods, and local life.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/gallery`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Photo Gallery', url: '/gallery' },
        ]),
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />
      <GalleryHero />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-4">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-teal-600 transition-colors cursor-pointer">
            Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <span className="text-gray-900 font-medium">Photo Gallery</span>
        </nav>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 pb-24">
        <GalleryGrid />

        {/* Submit CTA */}
        <div className="mt-20 relative rounded-2xl overflow-hidden bg-gradient-to-r from-teal-700 to-teal-900 p-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-600/30 rounded-full -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-1/3 w-40 h-40 bg-amber-500/10 rounded-full translate-y-1/2"></div>
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white">
              <h3 className="text-2xl font-bold mb-2">Share Your Roseville Photos</h3>
              <p className="text-white/80 max-w-lg text-sm">
                Have a great photo of Roseville? We&apos;d love to feature it in our community
                gallery. Submit your best shots and share the beauty of our city.
              </p>
            </div>
            <Link
              to="/submit-business"
              className="px-6 py-3 bg-white text-teal-800 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Submit a Photo
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
